import { Box, Flex, Image, Text } from '@chakra-ui/react';
import { YesNo } from '@features/prediction-markets/OutcomeRow';
import {
  BuySellSegment,
  LimitPriceControl,
  OrderTypePicker,
  SharesControl,
  TradeButton,
  TradeSummary,
  YesNoButtons,
  useTradeForm,
} from '@features/prediction-markets/TradeFormControls';
import { MarketOutcome } from '@features/prediction-markets/types';
import { memo } from 'react';

interface Props {
  outcome: MarketOutcome | null;
  commodityImage: string;
  yesNo: YesNo;
  onYesNoChange: (value: YesNo) => void;
}

const TradePanel = ({ outcome, commodityImage, yesNo, onYesNoChange }: Props) => {
  const form = useTradeForm();

  if (outcome == null) {
    return (
      <Box bg="surface.muted" borderRadius="xl" p={5} w="full">
        <Text color="text.secondary" fontSize="sm" textAlign="center">
          Select an outcome to trade
        </Text>
      </Box>
    );
  }

  const { total, toWin } = form.computeSummary(outcome, yesNo);

  return (
    <Box
      bg="surface.muted"
      borderRadius="xl"
      p={5}
      w="full"
      display="flex"
      flexDirection="column"
      gap={4}
    >
      <Flex alignItems="center" gap={2}>
        <Image src={commodityImage} alt="" boxSize="28px" borderRadius="full" />
        <Text fontSize="sm" fontWeight="semibold" color="text.primary" lineClamp={1}>
          {outcome.label}
        </Text>
      </Flex>

      <Flex justifyContent="space-between" alignItems="center">
        <BuySellSegment side={form.side} onSideChange={form.handleSideChange} />
        <OrderTypePicker
          orderType={form.orderType}
          onOrderTypeChange={form.handleOrderTypeChange}
        />
      </Flex>

      <YesNoButtons outcome={outcome} yesNo={yesNo} onYesNoChange={onYesNoChange} />

      {form.orderType === 'limit' && (
        <LimitPriceControl
          limitPrice={form.limitPrice}
          onInc={form.handleLimitPriceInc}
          onDec={form.handleLimitPriceDec}
        />
      )}

      <SharesControl
        shares={form.shares}
        onSharesChange={form.handleSharesChange}
        onQuickShares={form.handleQuickShares}
      />

      <TradeSummary total={total} toWin={toWin} />
      <TradeButton />

      <Text fontSize="2xs" color="text.secondary" textAlign="center">
        By trading, you agree to the Terms of Use.
      </Text>
    </Box>
  );
};

export default memo(TradePanel);
